/**
 * Настройки человека, доведённые до экрана (тикет T18b, спецификация § 14).
 *
 * Всю работу делает ядро клиента: оно ходит на сервер, показывает правку сразу, откатывает
 * при отказе и считает готовое оформление. Здесь только три вещи, которых ядру знать
 * нельзя (ADR-0002): держать это добро в памяти React, прикладывать оформление к
 * документу и слушать подсказки самого устройства.
 *
 * До входа Настроек ещё нет — и всё равно нужен язык и читаемый экран: человек видит
 * страницу входа раньше, чем сервер узнаёт, кто он. Тогда язык берётся у браузера, а
 * оформление собирается из умолчаний.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { createSettingsEditor } from '@core/settings/editor.js';
import type { SettingsEditor, SettingsState } from '@core/settings/editor.js';
import { createHttpSettingsGateway } from '@core/settings/http-gateway.js';
import { appearanceOf } from '@core/settings/appearance.js';
import type { Appearance, DeviceHints, Language } from '@core/settings/appearance.js';
import { applyAppearance, deviceHints, watchDeviceHints } from '../appearance/apply.js';
import { translatorFor } from '../i18n/dictionary.js';
import type { Translator } from '../i18n/dictionary.js';

const LANGUAGES: readonly Language[] = ['ru', 'en', 'he'];

/**
 * Язык браузера — только до входа. Незнакомый язык становится русским: русский здесь не
 * «главный», а тот, на котором заказана система, и человек всё равно должен что-то
 * прочитать, а не увидеть пустые кнопки.
 */
export const browserLanguage = (): Language => {
  if (typeof navigator === 'undefined') return 'ru';
  for (const raw of navigator.languages ?? [navigator.language]) {
    const first = String(raw ?? '').toLowerCase().split(/[-_]/)[0];
    if (first && (LANGUAGES as readonly string[]).includes(first)) return first as Language;
  }
  return 'ru';
};

interface SettingsValue {
  /** Ядро раздела. Ещё не вошли — `null`, и править нечего. */
  readonly editor: SettingsEditor | null;
  /** Что сейчас на экране. До входа — пустые Настройки с готовым оформлением. */
  readonly state: SettingsState;
  readonly appearance: Appearance;
  readonly language: Language;
  readonly t: Translator;
}

const SettingsContext = createContext<SettingsValue | null>(null);

/** Пустое состояние до входа: экран уже нужно чем-то оформить. */
const emptyState = (language: Language, hints: DeviceHints): SettingsState => ({
  settings: { language },
  ownKeys: [],
  ignoredKeys: [],
  groups: {},
  appearance: appearanceOf({ language }, hints),
  loading: false,
  saving: false,
  refusal: null,
});

export function SettingsProvider({
  spaceSlug,
  children,
}: {
  readonly spaceSlug: string | null;
  readonly children: ReactNode;
}): ReactNode {
  const [hints, setHints] = useState<DeviceHints>(() => deviceHints());

  const editor = useMemo(() => {
    if (!spaceSlug) return null;
    // Язык двери не задаём: сервер отдаёт Настройки, где язык человека и лежит, — назови
    // мы язык заранее, объяснения приходили бы на языке браузера, а не на выбранном.
    return createSettingsEditor(createHttpSettingsGateway({ baseUrl: '', spaceSlug }), hints);
    // Подсказки устройства меняются отдельным ходом (`updateHints`) — пересоздавать из-за
    // них весь раздел значило бы заново спрашивать сервер при каждой смене темы системы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceSlug]);

  const fallbackLanguage = browserLanguage();
  const [state, setState] = useState<SettingsState>(() => emptyState(fallbackLanguage, hints));

  useEffect(() => {
    if (!editor) {
      setState(emptyState(browserLanguage(), hints));
      return;
    }
    setState(editor.state());
    const stop = editor.subscribe(setState);
    void editor.load();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    const stop = watchDeviceHints((next) => setHints(next));
    return stop;
  }, []);

  useEffect(() => {
    if (editor) editor.updateHints(hints);
    else setState(emptyState(browserLanguage(), hints));
  }, [editor, hints]);

  const appearance = state.appearance;

  useEffect(() => {
    applyAppearance(appearance, document.documentElement);
  }, [appearance]);

  const value = useMemo<SettingsValue>(
    () => ({
      editor,
      state,
      appearance,
      language: appearance.language,
      t: translatorFor(appearance.language),
    }),
    [editor, state, appearance],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export const useSettings = (): SettingsValue => {
  const value = useContext(SettingsContext);
  if (!value) throw new Error('SettingsProvider забыли поставить выше по дереву');
  return value;
};

/** Короткий путь к переводчику: он нужен почти всем, а всё остальное — редко. */
export const useT = (): Translator => useSettings().t;
